<template>
  <main>
    <cdr-container
      class="breadcrumbs"
      data-ui="breadcrumbs"
      tag="section"
    >
      <cdr-breadcrumb
        ref="breadcrumbs"
        :truncation-enabled="false"
        :items="breadcrumbsConfig"
      />
    </cdr-container>
    <cdr-container
      class="course-header"
      data-ui="course-header"
      tag="section"
    >
      <cdr-text
        class="course-header__title"
        tag="h1"
      >
        {{ courseName }}
      </cdr-text>
      <bazaar-voice-component
        :session-data="sessionsIdMap"
        :product-id="productId"
        component-type="rating_summary"
        class="course-header__rating"
      />
    </cdr-container>
    <cdr-container
      class="hero"
      tag="section"
    >
      <course-hero-component
        :activity-level="activityLevel"
        :all-activity-levels="activityLevelsContent"
        :all-skill-levels="skillLevelsContent"
        :base-image-uri="baseImageUri"
        :course-attributes="courseAttributes"
        :course-name="courseName"
        :long-description="longDescription"
        :skill-level="skillLevel"
      />
    </cdr-container>
    <template v-if="!showChallenger">
      <cdr-container
        v-if="gearProvided"
        data-ui="gear-info-section"
        tag="section"
      >
        <gear-list-component
          class="course-gear-control"
          :gear-provided="gearProvided"
        />
      </cdr-container>
      <cdr-container
        class="location"
        tag="section"
      >
        <location-search-facet
          v-bind="locationConfig"
          @update-location="updateLocation"
          @remove-location="removeLocation"
        />
      </cdr-container>
      <cdr-container
        id="sessions-by-month"
        tag="section"
      >
        <filter-by-month-control-component
          :sessions-by-month="sessionsByMonth"
          :session-count="sessionCount"
        />
      </cdr-container>
    </template>
    <div v-else>
      <cdr-container
        v-if="gearProvided"
        tag="section"
        data-ui="gear-info-section"
      >
        <gear-list-component
          :show-challenger="true"
          :gear-provided="gearProvided"
        />
      </cdr-container>
      <cdr-container
        id="sessions-by-month"
        tag="section"
      >
        <filter-by-month-component
          :location="location(this)"
          :no-sessions="noSessions(this)"
          :sessions-by-month="sessions(this)"
          :session-count="sessionCount"
        />
      </cdr-container>
    </div>
    <cdr-container tag="section">
      <bazaar-voice-component
        :session-data="sessionsIdMap"
        :product-id="productId"
        component-type="reviews"
        class="bv-component__reviews"
      />
    </cdr-container>
  </main>
</template>

<script>
// TODO: Remove next line when A/B test ends and target wins
import { win } from '@rei/browser-shim';
import { CdrBreadcrumb, CdrText, CdrContainer } from '@rei/cedar';
import BazaarVoiceComponent from '../../components/BazaarVoiceComponent.vue';
import CourseHeroComponent from '../../components/CourseHeroComponent.vue';
import GearListComponent from '../../components/GearListComponent.vue';
// TODO: Remove next line when A/B test ends and target wins
import LocationSearchFacet from '../../components/LocationSearchFacet.vue';
// TODO: Remove next line when A/B test ends and target wins
import FilterByMonthControlComponent from '../../components/FilterByMonthControlComponent.vue';
import FilterByMonthComponent from '../../components/FilterByMonthComponent.vue';
import course from '../../mixins/course/course';
import getLevel from '../../mixins/getLevel';
import metrics from '../../mixins/metrics';
// TODO: Remove next line when A/B test ends and target wins
import constants from '../../common/constants';

export default {
  name: 'CourseDetailsPage',
  components: {
    BazaarVoiceComponent,
    CdrBreadcrumb,
    CdrText,
    CdrContainer,
    CourseHeroComponent,
    GearListComponent,
    FilterByMonthComponent,
    // // TODO: Remove next line when A/B test ends and target wins
    FilterByMonthControlComponent,
    // // TODO: Remove next line when A/B test ends and target wins
    LocationSearchFacet,
  },
  mixins: [course, getLevel, metrics],
  inheritAttrs: false,
  props: {
    activityLevels: {
      type: Array,
      required: true,
    },
    allActivityLevels: {
      type: Object,
      required: true,
    },
    allSkillLevels: {
      type: Object,
      required: true,
    },
    baseImageUri: {
      type: String,
      required: true,
    },
    breadcrumbs: {
      type: Object,
      required: true,
    },
    courseGearInfo: {
      type: Object,
      required: true,
    },
    courseName: {
      type: String,
      required: true,
    },
    distanceFilter: {
      type: [Number, String],
      required: true,
    },
    locationFilter: {
      type: String,
      required: true,
    },
    longDescription: {
      type: String,
      required: true,
    },
    minAge: {
      type: [Boolean, Number],
      required: false,
      default: false,
    },
    pageTitle: {
      type: String,
      required: true,
    },
    sessionsIdMap: {
      type: Object,
      required: true,
    },
    productId: {
      type: String,
      required: true,
    },
    sessionsByMonth: {
      type: Object,
      required: true,
    },
    sessionCount: {
      type: Number,
      required: true,
    },
    skillLevels: {
      type: Array,
      required: true,
    },
  },
  // TODO: Reove next property when the A/B test ends and the target wins
  data: () => ({
    customEvent: null,
    // TODO: change by the real event name
    eventName: 'REI_enable_sessions_challenger',
    showChallenger: false,
  }),
  computed: {
    activityLevel() {
      const { activityLevels: inputArr } = this;
      return this.getLevel({ type: 'activity-level', inputArr });
    },
    activityLevelsContent() {
      return this.pruneAttributes(this.allActivityLevels);
    },
    breadcrumbsConfig() {
      const { breadcrumbs = {} } = this;
      return Object.values(breadcrumbs).map(({ data: url, name }) => ({ item: { url, name } }));
    },
    courseAttributes() {
      const { activityLevel, minAge, skillLevel } = this;
      const courseAttributes = [];

      if (typeof minAge === 'number') {
        const heading = 'Participants';
        const subheading = `Age ${minAge} and up`;
        courseAttributes.push({
          type: 'min-age',
          heading,
          subheading,
          tip: false,
        });
      }

      if (skillLevel) courseAttributes.push(skillLevel);
      if (activityLevel) courseAttributes.push(activityLevel);

      return courseAttributes;
    },
    // TODO: Remove next function when A/B test ends and target wins
    locationConfig() {
      const radius = parseInt(this.distanceFilter, 10) || constants.SEARCH_RADIUS;
      return {
        location: this.locationFilter,
        radius,
        class: 'mobile-only',
        mobile: false,
        page: 'course',
      };
    },
    skillLevel() {
      const { skillLevels: inputArr } = this;
      return this.getLevel({ type: 'skill-level', inputArr });
    },
    skillLevelsContent() {
      return this.pruneAttributes(this.allSkillLevels);
    },
    gearProvided() {
      if (this.courseGearInfo.provided.length === 0) return false;
      return this.courseGearInfo.provided;
    },
  },
  mounted() {
    this.$nextTick(() => {
      const { breadcrumbs: nodeList } = this.$refs;
      const breadcrumbs = Array.from(nodeList.$el.querySelectorAll('a')) || [];
      breadcrumbs.forEach((crumb) => crumb.addEventListener('click', this.handleBreadcrumbClick));
    });

    // TODO: Remove next block when A/B test ends and target wins
    if (window && window.document && window.document.addEventListener) {
      const { eventName } = this;
      this.customEvent = new Event(eventName);
      window.document.addEventListener(eventName, this.enableSessionsChallenger);
      window.enableSessionsChallenger = this.customEvent;
    }
  },
  beforeUnmount() {
    const breadcrumbs = Array.from(this.$refs.breadcrumbs.querySelectorAll('a')) || [];
    breadcrumbs.forEach((crumb) => crumb.removeEventListener('click', this.handleBreadcrumbClick));
  },
  methods: {
    // TODO: Remove next block when A/B test ends and target wins
    enableSessionsChallenger() {
      if (window) {
        this.showChallenger = true;
        window.document.removeEventListener(this.customEvent, this.enableSessionsChallenger);
      }
    },
    handleBreadcrumbClick({ target }) {
      const linkName = `{{ pageName }}_breadcrumb_${target.innerText}`;
      this.metricsClick({ linkName });
    },
    pruneAttributes(obj) {
      return Object.keys(obj)
        .map((id) => ({ id, ...obj[id] }))
        .filter(({ level }) => level > 0)
        .sort((a, b) => (a.level > b.level ? 1 : -1), {});
    },
    // TODO: Remove next function when A/B test ends and target wins
    updateLocation(/* nextLocation, radius */) {
      // this is here as documentation only
      // add/update operations are exclusively handled by
      // @rei/location-search, LocationSearchFacet, and course-location-search
    },
    // TODO: Remove next function when A/B test ends and target wins
    removeLocation() {
      if (win.appObservable) {
        win.appObservable.getSearchResults({ searchString: null });
      }
    },
  },
};
</script>
